import { Lock, ChevronRight, Smartphone, Target, BarChart3, Shield, Send, Clock } from "lucide-react";

/** Realistic iPhone-style frame with bezel, dynamic island, side buttons and home indicator. */
const PhoneFrame = ({
  children,
  label,
  description,
  className = "",
  tilt = 0,
}: {
  children: React.ReactNode;
  label?: string;
  description?: string;
  className?: string;
  tilt?: number;
}) => (
  <div className={`relative flex flex-col items-center ${className}`}>
    <div
      className="relative mx-auto w-[270px] transition-transform duration-500 hover:-translate-y-2 hover:rotate-0"
      style={{ transform: `rotate(${tilt}deg)` }}
    >
      {/* outer glow */}
      <div className="pointer-events-none absolute -inset-6 -z-10 rounded-[3rem] bg-gradient-to-br from-primary/20 via-transparent to-primary/10 blur-2xl" />

      {/* side buttons */}
      <div className="absolute -left-[3px] top-[110px] h-8 w-[3px] rounded-l-sm bg-gradient-to-r from-black/80 to-neutral-700" />
      <div className="absolute -left-[3px] top-[160px] h-14 w-[3px] rounded-l-sm bg-gradient-to-r from-black/80 to-neutral-700" />
      <div className="absolute -left-[3px] top-[185px] h-14 w-[3px] rounded-l-sm bg-gradient-to-r from-black/80 to-neutral-700" />
      <div className="absolute -right-[3px] top-[150px] h-20 w-[3px] rounded-r-sm bg-gradient-to-l from-black/80 to-neutral-700" />

      {/* bezel */}
      <div className="relative rounded-[2.6rem] bg-gradient-to-br from-neutral-700 via-neutral-900 to-black p-[3px] shadow-[0_40px_90px_-20px_rgba(0,0,0,0.85),0_0_0_1px_rgba(255,255,255,0.04)_inset]">
        <div className="rounded-[2.45rem] bg-black p-[6px]">
          <div className="relative overflow-hidden rounded-[2.05rem] bg-gradient-to-b from-[hsl(265_50%_12%)] to-[hsl(280_45%_10%)] text-pitch-cream">
            {/* dynamic island */}
            <div className="absolute left-1/2 top-2 z-20 -translate-x-1/2 h-[26px] w-[95px] rounded-full bg-black shadow-[inset_0_0_0_1px_rgba(255,255,255,0.04)]">
              <div className="absolute right-3 top-1/2 h-1.5 w-1.5 -translate-y-1/2 rounded-full bg-neutral-800 ring-1 ring-neutral-700" />
            </div>
            {/* screen reflection */}
            <div className="pointer-events-none absolute inset-0 z-10 rounded-[2.05rem] bg-gradient-to-br from-white/[0.06] via-transparent to-transparent" />
            {/* status bar */}
            <div className="flex items-center justify-between px-5 pt-3 pb-1 text-[10px] font-semibold text-pitch-cream/80">
              <span>9:41</span>
              <span className="flex items-center gap-1">
                <span className="inline-block h-2 w-3 rounded-[1px] bg-pitch-cream/80" />
                <span className="inline-block h-2 w-1.5 rounded-[1px] bg-pitch-cream/80" />
                <span className="inline-block h-2 w-3 rounded-sm border border-pitch-cream/70" />
              </span>
            </div>
            <div className="px-3 pb-6 pt-7 relative z-0">{children}</div>
            {/* home indicator */}
            <div className="absolute bottom-1.5 left-1/2 z-10 h-[3px] w-[90px] -translate-x-1/2 rounded-full bg-pitch-cream/70" />
          </div>
        </div>
      </div>
    </div>
    {label && (
      <div className="mt-6 text-center">
        <div className="text-base font-semibold text-pitch-cream">{label}</div>
        {description && (
          <p className="mt-1.5 text-sm text-pitch-cream/55 max-w-[260px] leading-relaxed">{description}</p>
        )}
      </div>
    )}
  </div>
);

const MatchRow = ({
  home,
  away,
  flagH,
  flagA,
  time,
  pred,
  locked,
}: {
  home: string;
  away: string;
  flagH: string;
  flagA: string;
  time: string;
  pred?: string;
  locked?: boolean;
}) => (
  <div className="rounded-xl border border-pitch/60 bg-white/[0.03] p-2.5">
    <div className="flex items-center gap-2 text-xs">
      <div className="flex-1 flex items-center justify-end gap-1.5 min-w-0">
        <span className="font-semibold truncate">{home}</span>
        <span className="text-base leading-none">{flagH}</span>
      </div>
      <div className="min-w-[52px] text-center">
        {pred ? (
          <span className="font-bold tabular-nums text-primary">{pred}</span>
        ) : (
          <span className="font-bold tabular-nums">{time}</span>
        )}
      </div>
      <div className="flex-1 flex items-center gap-1.5 min-w-0">
        <span className="text-base leading-none">{flagA}</span>
        <span className="font-semibold truncate">{away}</span>
      </div>
      {locked && <Lock className="h-3 w-3 text-pitch-cream/50" />}
    </div>
  </div>
);

const TipsScreen = () => (
  <div className="space-y-2.5">
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2">
        <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/15">
          <Target className="h-3.5 w-3.5 text-primary" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-primary">Skupina A</div>
          <div className="text-sm font-bold leading-tight">Tipuj zápasy</div>
        </div>
      </div>
      <div className="text-[10px] rounded-full bg-primary/20 text-primary px-2 py-0.5 font-semibold">
        Živě
      </div>
    </div>
    <MatchRow home="Česko" away="USA" flagH="🇨🇿" flagA="🇺🇸" time="21:00" pred="2:1" />
    <MatchRow home="Mexiko" away="Kanada" flagH="🇲🇽" flagA="🇨🇦" time="18:00" pred="1:1" locked />
    <MatchRow home="Brazílie" away="Anglie" flagH="🇧🇷" flagA="🏴󠁧󠁢󠁥󠁮󠁧󠁿" time="20:30" />
    <MatchRow home="Německo" away="Francie" flagH="🇩🇪" flagA="🇫🇷" time="21:00" />
    <button className="w-full mt-2 rounded-lg bg-primary text-primary-foreground py-2 text-xs font-bold">
      Uložit tipy
    </button>
  </div>
);

const LeaderboardScreen = () => {
  const rows = [
    { rank: 1, name: "Pepa", pts: 47, you: false },
    { rank: 2, name: "Ty", pts: 42, you: true },
    { rank: 3, name: "Honza", pts: 38, you: false },
    { rank: 4, name: "Eva", pts: 35, you: false },
    { rank: 5, name: "Tom", pts: 31, you: false },
    { rank: 6, name: "Káťa", pts: 28, you: false },
  ];
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 mb-3">
        <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/15">
          <BarChart3 className="h-3.5 w-3.5 text-primary" />
        </div>
        <div>
          <div className="text-sm font-bold leading-tight">Žebříček ligy</div>
          <div className="text-[10px] text-pitch-cream/50">Kámoši z práce · 8 hráčů</div>
        </div>
      </div>
      {rows.map((r) => (
        <div
          key={r.rank}
          className={`flex items-center justify-between rounded-lg px-2.5 py-2 text-xs ${
            r.you ? "bg-primary/15 border border-primary/40" : "bg-white/[0.03]"
          }`}
        >
          <div className="flex items-center gap-2">
            <span
              className={`w-5 text-center font-bold ${
                r.rank === 1 ? "text-primary" : "text-pitch-cream/60"
              }`}
            >
              {r.rank}.
            </span>
            <span className={`font-semibold ${r.you ? "text-primary" : ""}`}>
              {r.name}
            </span>
          </div>
          <span className="font-bold tabular-nums">{r.pts} b</span>
        </div>
      ))}
    </div>
  );
};

const LeaguesScreen = () => (
  <div className="space-y-2.5">
    <div className="flex items-center gap-2 mb-3">
      <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/15">
        <Shield className="h-3.5 w-3.5 text-primary" />
      </div>
      <div>
        <div className="text-sm font-bold leading-tight">Moje ligy</div>
        <div className="text-[10px] text-pitch-cream/50">3 aktivní ligy</div>
      </div>
    </div>
    {[
      { name: "Kámoši z práce", members: 8, rank: "2." },
      { name: "Rodina", members: 5, rank: "1." },
      { name: "Hospoda U Tří lvů", members: 12, rank: "4." },
    ].map((l) => (
      <div
        key={l.name}
        className="flex items-center justify-between rounded-xl border border-pitch/60 bg-white/[0.03] p-3"
      >
        <div className="min-w-0">
          <div className="text-xs font-semibold truncate">{l.name}</div>
          <div className="text-[10px] text-pitch-cream/50">{l.members} hráčů · {l.rank} místo</div>
        </div>
        <ChevronRight className="h-4 w-4 text-primary shrink-0" />
      </div>
    ))}
    <button className="w-full mt-2 rounded-lg border border-pitch text-pitch-cream py-2 text-xs font-semibold">
      + Založit novou ligu
    </button>
  </div>
);

const MyTipsScreen = () => {
  return (
    <div className="space-y-2.5">
      <div className="flex items-center gap-2 mb-3">
        <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/15">
          <Send className="h-3.5 w-3.5 text-primary" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-primary">Skupina A</div>
          <div className="text-sm font-bold leading-tight">Moje tipy</div>
        </div>
      </div>

      {/* Match 1 — open for tipping */}
      <div className="rounded-xl border border-pitch/60 bg-white/[0.03] p-2.5">
        <div className="flex items-center justify-between text-[10px] text-pitch-cream/50 mb-1.5">
          <span>Dnes 21:00</span>
          <span className="flex items-center gap-1 text-primary">
            <Clock className="h-3 w-3" /> Zamkne se za 48 min
          </span>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <div className="flex-1 flex items-center justify-end gap-1.5 min-w-0">
            <span className="font-semibold truncate">Česko</span>
            <span className="text-base leading-none">🇨🇿</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-7 h-7 rounded-md bg-white/[0.06] border border-pitch/60 flex items-center justify-center font-bold text-sm">2</div>
            <span className="text-pitch-cream/40 font-bold">:</span>
            <div className="w-7 h-7 rounded-md bg-white/[0.06] border border-pitch/60 flex items-center justify-center font-bold text-sm">1</div>
          </div>
          <div className="flex-1 flex items-center gap-1.5 min-w-0">
            <span className="text-base leading-none">🇺🇸</span>
            <span className="font-semibold truncate">USA</span>
          </div>
        </div>
        <button className="w-full mt-2 rounded-lg bg-primary text-primary-foreground py-1.5 text-[11px] font-bold">
          Uložit tip
        </button>
      </div>

      {/* Match 2 — almost locked */}
      <div className="rounded-xl border border-pitch/60 bg-white/[0.03] p-2.5">
        <div className="flex items-center justify-between text-[10px] text-pitch-cream/50 mb-1.5">
          <span>Zítra 18:00</span>
          <span className="flex items-center gap-1 text-amber-400">
            <Clock className="h-3 w-3" /> Zamkne se za 12 min
          </span>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <div className="flex-1 flex items-center justify-end gap-1.5 min-w-0">
            <span className="font-semibold truncate">Mexiko</span>
            <span className="text-base leading-none">🇲🇽</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-7 h-7 rounded-md bg-white/[0.06] border border-pitch/60 flex items-center justify-center font-bold text-sm">0</div>
            <span className="text-pitch-cream/40 font-bold">:</span>
            <div className="w-7 h-7 rounded-md bg-white/[0.06] border border-pitch/60 flex items-center justify-center font-bold text-sm">0</div>
          </div>
          <div className="flex-1 flex items-center gap-1.5 min-w-0">
            <span className="text-base leading-none">🇨🇦</span>
            <span className="font-semibold truncate">Kanada</span>
          </div>
        </div>
        <button className="w-full mt-2 rounded-lg bg-primary text-primary-foreground py-1.5 text-[11px] font-bold">
          Uložit tip
        </button>
      </div>

      {/* Match 3 — locked */}
      <div className="rounded-xl border border-pitch/40 bg-white/[0.02] p-2.5 opacity-70">
        <div className="flex items-center justify-between text-[10px] text-pitch-cream/40 mb-1.5">
          <span>Včera 20:30</span>
          <span className="flex items-center gap-1">
            <Lock className="h-3 w-3" /> Zamčeno
          </span>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <div className="flex-1 flex items-center justify-end gap-1.5 min-w-0">
            <span className="font-semibold truncate text-pitch-cream/60">Brazílie</span>
            <span className="text-base leading-none">🇧🇷</span>
          </div>
          <div className="min-w-[52px] text-center">
            <span className="font-bold tabular-nums text-primary">1:2</span>
          </div>
          <div className="flex-1 flex items-center gap-1.5 min-w-0">
            <span className="text-base leading-none">🏴󠁧󠁢󠁥󠁮󠁧󠁿</span>
            <span className="font-semibold truncate text-pitch-cream/60">Anglie</span>
          </div>
        </div>
        <div className="w-full mt-2 rounded-lg bg-white/[0.03] border border-pitch/40 text-pitch-cream/40 py-1.5 text-[11px] font-semibold text-center">
          Tip uložen
        </div>
      </div>
    </div>
  );
};

export const AppPreviewMockups = () => {
  return (
    <section className="container mx-auto px-4 py-24">
      <div className="text-center max-w-2xl mx-auto mb-16">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-pitch text-xs uppercase tracking-widest text-primary mb-5">
          <Smartphone className="h-3 w-3" /> Mobil-first aplikace
        </div>
        <h2 className="text-3xl md:text-5xl font-bold mb-4">
          Vše po ruce. <span className="text-primary">Vždy.</span>
        </h2>
        <p className="text-pitch-cream/65">
          Nainstaluj si Tipomatic na plochu telefonu — funguje jako nativní appka,
          žádný app store, žádné stahování.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-10 lg:gap-6 items-start max-w-6xl mx-auto">
        <PhoneFrame
          label="Tipuj za 10 sekund"
          description="Vyber skóre zápasu, klepni a hotovo. Tipy se zamknou před výkopem."
          tilt={-3}
        >
          <TipsScreen />
        </PhoneFrame>
        <PhoneFrame
          label="Moje tipy"
          description="Odevzdej tip před výkopem. Jakmile se zápas zamkne, tip už nejde změnit."
          className="lg:mt-12"
          tilt={2}
        >
          <MyTipsScreen />
        </PhoneFrame>
        <PhoneFrame
          label="Žebříček ligy"
          description="Sleduj, jak si vedeš proti kamarádům. Body se počítají automaticky."
          tilt={-2}
        >
          <LeaderboardScreen />
        </PhoneFrame>
        <PhoneFrame
          label="Soukromé ligy"
          description="Založ ligu, pozvi partu kódem a hrajte jen mezi sebou."
          className="lg:mt-12"
          tilt={3}
        >
          <LeaguesScreen />
        </PhoneFrame>
      </div>
    </section>
  );
};
